import React, { useEffect, useState } from "react";
import { useRouter } from "next/router";
import useEmblaCarousel from "embla-carousel-react";
import classes from "./SuggestedServices.module.scss";
import CustomImage from "../../CustomImage";
import { isFrappeOrJotFormClass } from "../../../utils/helpers";

export interface SuggestedServiceItem {
  id: string;
  image: string;
  imageAlt: string;
  title: string;
  description: string;
  href: string;
}

interface SuggestedServicesProps {
  heading?: string;
  subtext?: string;
  services?: SuggestedServiceItem[];
  onLearnMoreClick?: (service: SuggestedServiceItem) => void;
  alliedServicesCardData?: any;
  containerStyles?: React.CSSProperties;
  excludeCurrentSlug?: boolean;
}

const SuggestedServices = ({
  heading = "",
  subtext = "",
  onLearnMoreClick,
  alliedServicesCardData,
  containerStyles = {},
  excludeCurrentSlug = false,
}: SuggestedServicesProps) => {
  const router = useRouter();
  const { slug } = router.query;

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, align: "start" });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  useEffect(() => {
    if (!emblaApi) return;
    const updateScrollState = () => {
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
    };
    updateScrollState();
    emblaApi.on("select", updateScrollState);
    emblaApi.on("reInit", updateScrollState);
    return () => {
      emblaApi.off("select", updateScrollState);
      emblaApi.off("reInit", updateScrollState);
    };
  }, [emblaApi, alliedServicesCardData]);

  const goToPrev = () => emblaApi?.scrollPrev();
  const goToNext = () => emblaApi?.scrollNext();

  const filteredCardData = excludeCurrentSlug ? alliedServicesCardData?.filter((card: any) => card.slug !== slug) : alliedServicesCardData;

  const learnMoreClickHandler = (serviceBtn: any) => () => {
    onLearnMoreClick?.(serviceBtn);

    if (serviceBtn?.link) {
      window.open(serviceBtn?.link, "_blank");
    }
  };

  return (
    <div className={classes["suggested-services-container"]} style={containerStyles}>
      <h2 className={classes["heading"]}>{heading}</h2>
      <p className={classes["subtext"]}>{subtext}</p>

      <div className={classes["cards-container"]}>
        <div className={classes.viewport} ref={emblaRef}>
          <div className={classes["cards-slider"]}>
            {filteredCardData?.length > 0 &&
              filteredCardData?.map((service: any) => (
                <div className={classes.slide} key={service.id}>
                  <div style={{ backgroundColor: service?.card?.cardBgColor ?? "" }} className={classes.card}>
                    <div className={classes["card-image-wrap"]}>
                      <CustomImage
                        src={service?.card?.img?.url}
                        alt={service?.card?.img?.name}
                        layout="fill"
                        objectFit="cover"
                        className={classes["card-image"]}
                        width={278}
                        height={164}
                      />
                    </div>

                    <div className={classes["content-container"]}>
                      <div className={classes["card-text"]}>
                        <h3 className={classes["card-title"]}>{service?.card?.title}</h3>
                        <p className={classes["card-description"]}>{service?.card?.description}</p>
                      </div>

                      <div className={classes["card-footer"]}>
                        {service?.card?.footerBtn?.title && (
                          <span
                            className={`${classes["learn-more-link"]} ${service?.card?.footerBtn?.link ? "" : isFrappeOrJotFormClass(service?.card?.footerBtn?.formId ?? "")}`}
                            onClick={learnMoreClickHandler(service?.card?.footerBtn)}
                          >
                            {service?.card?.footerBtn?.title}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      <div className={classes["arrows-container"]}>
        <div
          className={`${classes["btcircle"]}${canScrollPrev ? "" : ` ${classes["btcircle-disabled"]}`}`}
          onClick={canScrollPrev ? goToPrev : undefined}
          aria-disabled={!canScrollPrev}
        >
          <CustomImage src="/new-images/left-arrow.svg" height={17} width={10} alt="left-arrow" />
        </div>

        <div
          className={`${classes["btcircle"]}${canScrollNext ? "" : ` ${classes["btcircle-disabled"]}`}`}
          onClick={canScrollNext ? goToNext : undefined}
          aria-disabled={!canScrollNext}
        >
          <CustomImage src="/new-images/right-arrow.svg" height={17} width={10} alt="right-arrow" />
        </div>
      </div>
    </div>
  );
};

export default SuggestedServices;
